import { err, ok, Result } from "neverthrow";
import GraphqlApiError from "@/framework/network/gql/GraphqlApiError";
import { GraphqlApi, IGraphqlApi } from "@/framework/network/gql/GraphqlApi";
import {
  CancelGuestInquiryForwardDocument,
  CancelGuestInquiryForwardMutation,
  CompleteGuestInquiryDocument,
  CompleteGuestInquiryMutationVariables,
  CompleteVoiceCallDocument,
  CompleteVoiceCallMutation,
  CreateInquiryForwardDocument,
  CreateInquiryForwardMutation,
  CreateVoiceCallTokenDocument,
  CreateVoiceCallTokenMutation,
  GeneralResponse,
  GeneralResult,
  GetEarliestInquiryInfoDocument,
  GetEarliestInquiryInfoQuery,
  GetInquiryInfoDocument,
  GetInquiryInfoQuery,
  GetStaffMyselfQuery,
  ListAssignedInquiriesDocument,
  ListAssignedInquiriesQuery,
  QcBizStaff,
  RejectInquiryDocument,
  RejectInquiryMutation,
  RespondGuestInquiryDocument,
  RespondGuestInquiryForwardDocument,
  RespondGuestInquiryForwardMutation,
  RespondGuestInquiryMutation,
  RespondVoiceCallDocument,
  RespondVoiceCallMutation,
  SetLoggedInResponderDocument,
  SetLoggedInResponderMutation,
  TakeOverInquiryDocument,
  TakeOverInquiryMutation,
  UnassignedInquiriesDocument,
  UnassignedInquiriesQuery,
} from "@/framework/network/gql/generated/graphql";
import {
  EarliestInquiryInfo,
  EarliestInquiryInfoSchema,
  InquiryInfo,
  InquiryInfoSchema,
  ListAssignedInquiriesResponse,
  ListAssignedInquiriesResponseSchema,
  RespondHotelGuestResponse,
  RespondHotelGuestResponseSchema,
  RespondInquiryForwardResponse,
  RespondInquiryForwardResponseSchema,
  RespondInquiryResponse,
  RespondInquiryResponseSchema,
} from "@/modules/staff/objects/conversation/InquiryObjects";
import { CustomGetStaffMyselfResponder } from "@/framework/network/gql/custom/GetStaffMyself/CustomGetStaffMyselfResponder";
import { GeneralResultSchema } from "@/framework/objects/GeneralResult";
import { GeneralResponseSchema } from "@/framework/objects/GeneralResponse";
import { VoiceChatToken, VoiceChatTokenSchema } from "@/modules/staff/objects/conversation/VoiceChatToken";
import { z } from "zod";

export class InquiryService {
  private _graphql: IGraphqlApi;

  public constructor(graphql: IGraphqlApi = new GraphqlApi()) {
    this._graphql = graphql;
  }

  async rejectTheInquiry(
    conversationId: string,
    langCd: string,
    rejectComment: string
  ): Promise<Result<GeneralResult, GraphqlApiError>> {
    const v = { conversationId, langCd, rejectComment };
    const res = await this._graphql.runMutationAndValidate<RejectInquiryMutation, GeneralResult>(
      RejectInquiryDocument,
      GeneralResultSchema,
      v
    );
    if (res.isErr()) {
      return err(res.error);
    } else {
      return ok(res.value);
    }
  }

  async takeOverTheInquiry(inquiry_id: number): Promise<Result<GeneralResponse, GraphqlApiError>> {
    const result = await this._graphql.runMutationAndValidate<TakeOverInquiryMutation, GeneralResponse>(
      TakeOverInquiryDocument,
      GeneralResponseSchema,
      { inquiry_id }
    );
    if (result.isErr()) {
      return err(result.error);
    } else {
      return ok(result.value);
    }
  }

  async respondForwardGuestInquiry(
    inquiry_id: number,
    can_respond: boolean
  ): Promise<Result<RespondInquiryForwardResponse, GraphqlApiError>> {
    const v = { inquiry_id, can_respond };
    const result = await this._graphql.runMutationAndValidate<
      RespondGuestInquiryForwardMutation,
      RespondInquiryForwardResponse
    >(RespondGuestInquiryForwardDocument, RespondInquiryForwardResponseSchema, v);
    if (result.isErr()) {
      return err(result.error);
    } else {
      return ok(result.value);
    }
  }

  async respondGuestInquiry(
    inquiryId: string,
    responderId: number,
    canRespond: boolean
  ): Promise<Result<RespondHotelGuestResponse, GraphqlApiError>> {
    const v = {
      inquiryId: inquiryId.toString(),
      canRespond,
      responderId,
    };
    const result = await this._graphql.runMutationAndValidate<RespondGuestInquiryMutation, RespondHotelGuestResponse>(
      // @ts-ignore conflict between generic typing and exact requirement
      RespondGuestInquiryDocument,
      RespondHotelGuestResponseSchema,
      v
    );
    if (result.isErr()) {
      return err(result.error);
    } else {
      return ok(result.value);
    }
  }

  async respondVoiceChat(
    sessionId: string | null,
    inquiryId: string
  ): Promise<Result<RespondInquiryResponse, GraphqlApiError>> {
    // because stupidly, yet another ID can be nullable, according to BE
    if (!sessionId)
      return err(
        new GraphqlApiError(
          GraphqlApiError.Type.badRequest,
          undefined,
          undefined,
          "SessionId is null; cannot respond to voice chat request"
        )
      );

    const v = { sessionId, inquiryId: inquiryId.toString() };
    const result = await this._graphql.runMutationAndValidate<RespondVoiceCallMutation, RespondInquiryResponse>(
      // @ts-ignore conflict between generic typing and exact requirement
      RespondVoiceCallDocument,
      RespondInquiryResponseSchema,
      v
    );

    if (result.isErr()) {
      return err(result.error);
    } else {
      return ok(result.value);
    }
  }

  async completeCall(conversationId: string, responderId: number): Promise<Result<GeneralResult, GraphqlApiError>> {
    const v = { conversationId, responderId };
    const result = await this._graphql.runMutationAndValidate<CompleteVoiceCallMutation, GeneralResult>(
      // @ts-ignore conflict between generic typing and exact requirement
      CompleteVoiceCallDocument,
      GeneralResultSchema,
      v
    );

    if (result.isErr()) {
      return err(result.error);
    } else {
      return ok(result.value);
    }
  }

  async cancelGuestInquiryForward(inquiryId: number): Promise<Result<GeneralResponse, GraphqlApiError>> {
    const result = await this._graphql.runMutationAndValidate<CancelGuestInquiryForwardMutation, GeneralResponse>(
      CancelGuestInquiryForwardDocument,
      GeneralResponseSchema,
      { inquiry_id: inquiryId }
    );

    if (result.isErr()) {
      return err(result.error);
    } else {
      return ok(result.value);
    }
  }

  async createInquiryForward(
    inquiryId: number,
    targetStaffGroupId?: number,
    targetStaffId?: number
  ): Promise<Result<GeneralResponse, GraphqlApiError>> {
    const result = await this._graphql.runMutationAndValidate<CreateInquiryForwardMutation, GeneralResponse>(
      CreateInquiryForwardDocument,
      GeneralResponseSchema,
      { inquiryId: inquiryId, targetStaffGroupId: targetStaffGroupId, targetStaffId: targetStaffId }
    );

    if (result.isErr()) {
      return err(result.error);
    } else {
      return ok(result.value);
    }
  }

  async completeGuestInquiry(
    conversationId: string,
    responderId: number,
    isVoiceChatFinish?: boolean | undefined,
    guestTenantId?: number | undefined,
    closeChatState = "in_chat"
  ): Promise<Result<GeneralResponse, GraphqlApiError>> {
    const result = await this._graphql.runMutationAndValidate<CompleteGuestInquiryMutationVariables, GeneralResponse>(
      // @ts-ignore conflict between generic typing and exact requirement
      CompleteGuestInquiryDocument,
      GeneralResponseSchema,
      {
        conversation_id: conversationId,
        responder_id: responderId,
        is_voice_chat_finish: isVoiceChatFinish,
        guest_tenant_id: guestTenantId,
        close_chat_state: closeChatState,
      } as CompleteGuestInquiryMutationVariables
    );

    if (result.isErr()) {
      return err(result.error);
    } else {
      return ok(result.value);
    }
  }

  async getInquiry(): Promise<Result<EarliestInquiryInfo, GraphqlApiError>> {
    const res = await this._graphql.runQueryAndValidate<GetEarliestInquiryInfoQuery, EarliestInquiryInfo>(
      // @ts-ignore conflict between generic typing and exact requirement
      GetEarliestInquiryInfoDocument,
      EarliestInquiryInfoSchema,
      {},
      true
    );
    if (res.isErr()) {
      return err(res.error);
    } else {
      return ok(res.value);
    }
  }

  async getInquiryInfo(inquiry_id: string): Promise<Result<InquiryInfo, GraphqlApiError>> {
    const res = await this._graphql.runQueryAndValidate<GetInquiryInfoQuery, InquiryInfo>(
      GetInquiryInfoDocument,
      InquiryInfoSchema,
      { inquiry_id },
      true
    );
    if (res.isErr()) {
      return err(res.error);
    } else {
      return ok(res.value);
    }
  }

  async hasAssignedInquiries(): Promise<Result<ListAssignedInquiriesResponse, GraphqlApiError>> {
    const res = await this._graphql.runQueryAndValidate<ListAssignedInquiriesQuery, ListAssignedInquiriesResponse>(
      // @ts-ignore conflict between generic typing and exact requirement
      ListAssignedInquiriesDocument,
      ListAssignedInquiriesResponseSchema,
      {},
      true
    );
    if (res.isErr()) {
      return err(res.error);
    } else {
      return ok(res.value);
    }
  }

  async getVoiceChatToken(sessionId: string): Promise<Result<VoiceChatToken, GraphqlApiError>> {
    const res = await this._graphql.runMutationAndValidate<CreateVoiceCallTokenMutation, VoiceChatToken>(
      // @ts-ignore conflict between generic typing and exact requirement
      CreateVoiceCallTokenDocument,
      VoiceChatTokenSchema,
      { sessionId: sessionId }
    );
    return res.isErr() ? err(res.error) : ok(res.value);
  }

  async setResponder(responderId: number): Promise<Result<GeneralResult, GraphqlApiError>> {
    const res = await this._graphql.runMutationAndValidate<SetLoggedInResponderMutation, GeneralResult>(
      SetLoggedInResponderDocument,
      GeneralResultSchema,
      { responderId }
    );
    if (res.isErr()) {
      return err(res.error);
    } else {
      return ok(res.value);
    }
  }

  async getResponder(): Promise<Result<QcBizStaff, GraphqlApiError>> {
    const res = await this._graphql.runQuery(CustomGetStaffMyselfResponder, {});
    if (res.isErr()) {
      return err(res.error);
    } else {
      return ok(res.value.getStaffMyself);
    }
  }

  async getUnassignedInquiries(): Promise<Result<string[] | null, GraphqlApiError>> {
    const res = await this._graphql.runQueryAndValidate<UnassignedInquiriesQuery, string[] | null>(
      // @ts-ignore conflict between generic typing and exact requirement
      UnassignedInquiriesDocument,
      z.string().nullable(),
      {},
      true
    );
    if (res.isErr()) {
      return err(res.error);
    } else {
      return ok(res.value);
    }
  }
}
